<script lang="ts">
import {defineComponent} from 'vue';
import {event} from '../../analytics.ts';
import AllSpiceDiffSnippetView from './AllSpiceDiffSnippetView.vue';
import {isProjectViewEnabled} from '../../allspice.ts';
import {Snippet} from '../../features/snippet.ts';
import type {Container} from '../../container.ts';

const observerSupported = window && 'IntersectionObserver' in window;

export type AllSpiceDiffSnippetData = {
  display: boolean,
  observer: IntersectionObserver | null,
};

export default defineComponent({
  name: 'AllSpiceDiffSnippet',
  components: {'allspice-diff-snippet-view': AllSpiceDiffSnippetView},
  inject: {
    untypedContainer: {from: 'container'},
  },
  props: {
    // True if displaying a diff between two commits, false if viewing a file.
    // When in view file mode, use the head parameters to specify commit, tree
    // path, etc.
    isDiff: {
      type: Boolean,
      required: false,
      default: true,
    },
    repositoryOwner: {
      type: String,
      required: true,
      default: '',
    },
    repositoryName: {
      type: String,
      required: true,
      default: '',
    },
    headTreePath: {
      type: String,
      required: true,
      default: '',
    },
    headCommit: {
      type: String,
      required: true,
      default: '',
    },
    baseTreePath: {
      type: String,
      required: false,
      default: '',
    },
    baseCommit: {
      type: String,
      required: false,
      default: '',
    },
    // Value is either three dots or two dots.
    diffType: {
      type: String,
      required: false,
      default: '...',
    },
    // Alt text for accessibility.
    alt: {
      type: String,
      required: false,
      default: '',
    },
    // The SVG class(es): as-snippet, as-thumbnail.
    svgClassNames: {
      type: String,
      required: false,
      default: '',
    },
    // Layer IDs to display.
    layerIds: {
      type: String,
      required: false,
      default: null,
    },
    // diff layers to show
    diffVisibility: {
      type: String,
      required: false,
      default: 'full',
    },
    docId: {
      type: String,
      required: false,
      default: '',
    },
    // Variant to show
    variant: {
      type: String,
      required: false,
      default: 'default',
    },
    dataAsViewCoords: {
      type: String,
      required: false,
      default: '',
    },
    // Width-to-height aspect ratio.
    dataAsAspectRatio: {
      type: String,
      required: false,
      default: '',
    },
    // Whether the snippet was taken when the PCB was flipped
    isFlipped: {
      type: Boolean,
      required: false,
      default: false,
    },
    // When true and loading the image errors, displays a default image.
    displayDefaultOnError: {
      type: Boolean,
      required: false,
      default: false,
    },
    lazyLoad: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data(): AllSpiceDiffSnippetData {
    return {
      display: !(observerSupported && this.lazyLoad),
      observer: null,
    };
  },
  computed: {
    container(): Container {
      return this.untypedContainer as unknown as Container;
    },
    topLevelClassNames() {
      const svgClasses = (this.svgClassNames || '').split(' ');
      const names = ['as-diff-outer'];
      if (svgClasses.includes('as-thumbnail')) {
        names.push('thumbnail-outer');
      }
      return names;
    },

    snippet() {
      return Snippet.parse(this.dataAsViewCoords, this.dataAsAspectRatio);
    },

    style() {
      // Add internal margins to avoid layout changes
      return {
        height: `${(this.snippet.height + 2 + 7).toFixed(1)}px`,
        width: `${(this.snippet.width + 2 + 14).toFixed(1)}px`,
      };
    },

  },
  mounted() {
    const clickThruEvent = new CustomEvent('allspice-snippet-click-thru', {
      bubbles: true,
      detail: {
        docId: this.docId,
      },
    });
    // if we are already on the correct page, prevent reloading on clickthrough
    const anchorElement = this.$el.parentElement;
    let href = anchorElement.getAttribute('href');
    const appSubUrl = this.container.lookup('appSubUrl');
    let hashDifference = false;

    if (isProjectViewEnabled()) {
      try {
        const url = new URL(href);
        const anchorParam = url.searchParams.get('anchor');
        if (anchorParam) {
          const hash = `#${anchorParam}${this.docId ? `_${this.docId}` : ''}`;
          href += hash;
          if (hash !== window.location.hash) {
            hashDifference = true;
          }
        }
      } catch (e) {
        console.error('Error parsing URL', e);
      }
    }

    // Don't lazy load new comments, since they will be reused between pages
    let lazyLoad = this.lazyLoad;
    if (this.$el.closest('.add-comment')) {
      lazyLoad = false;
      this.display = true;
    }

    if (lazyLoad && observerSupported) {
      this.observer = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            this.display = true;
            return;
          }
        }
        this.display = false;
      });
      this.observer.observe(this.$el);
    }

    const hrefPathname = new URL(href, window.location.origin + appSubUrl).pathname;
    if (hrefPathname !== window.location.pathname || hashDifference) return;
    anchorElement.addEventListener('click', (e: MouseEvent) => {
      e.preventDefault();
      event('snippet_navigate');
      // Update window url without reloading
      window.history.pushState({}, '', href);
      // Trigger custom event to update the snippet reference
      document.dispatchEvent(clickThruEvent);
    });
  },
  unmounted() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },

});
</script>
<template>
  <div :class="topLevelClassNames">
    <allspice-diff-snippet-view
      v-if="display"
      :is-diff="isDiff"
      :repository-owner="repositoryOwner"
      :repository-name="repositoryName"
      :head-tree-path="headTreePath"
      :head-commit="headCommit"
      :base-tree-path="baseTreePath"
      :base-commit="baseCommit"
      :diff-type="diffType"
      :alt="alt"
      :svg-class-names="svgClassNames"
      :layer-ids="layerIds"
      :diff-visibility="diffVisibility"
      :doc-id="docId"
      :variant="variant"
      :data-as-view-coords="dataAsViewCoords"
      :data-as-aspect-ratio="dataAsAspectRatio"
      :is-flipped="isFlipped"
      :display-default-on-error="displayDefaultOnError"
    />
    <div
      v-if="!display"
      :style="style"
    />
  </div>
</template>
